import type { FoundryV2CompositionDraftRecord } from "../api/foundryV2Client";

type Props = {
  compositionDraft: FoundryV2CompositionDraftRecord;
};

function blockerTone(severity: "blocking" | "watch"): string {
  return severity === "blocking"
    ? "border-rose-200 bg-rose-50 text-rose-700"
    : "border-amber-200 bg-amber-50 text-amber-700";
}

function postureTone(status: "build-ready" | "needs-input" | "blocked" | "stable" | "watch" | "ready"): string {
  if (status === "build-ready" || status === "stable" || status === "ready") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (status === "blocked") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  return "border-amber-200 bg-amber-50 text-amber-700";
}

export function FoundryAssemblyContractPanel({ compositionDraft }: Props): JSX.Element {
  const { foundryContract, foundryContractV3, assemblyPlan } = compositionDraft;
  const productionBlockers =
    foundryContractV3?.buildReadyDecision.productionBlockers ??
    foundryContract.buildBlockers.filter((blocker) => blocker.severity === "blocking");
  const watchBlockers =
    foundryContract.buildBlockers.filter((blocker) => blocker.severity === "watch");
  const runtimeConstraints = foundryContractV3?.buildReadyDecision.runtimeConstraints ?? [];
  const customDeltaTruth = foundryContractV3?.buildReadyDecision.customDelta ?? {
    summary: foundryContract.customDeltaContract.summary,
    load: foundryContract.customDeltaContract.blockedUntil.length ? "elevated" : "standard",
    openQuestions: foundryContract.customDeltaContract.blockedUntil,
    customUiRequirements: foundryContract.customDeltaContract.customUiRequirements,
    customIntegrations: foundryContract.customDeltaContract.customIntegrations,
  };
  const contractPosture =
    foundryContractV3?.buildReadyDecision.status ??
    (foundryContract.buildBlockers.some((blocker) => blocker.severity === "blocking") ? "blocked" : "needs-input");
  const handoffStability = foundryContractV3?.buildReadyDecision.handoffStability ?? {
    status: productionBlockers.length ? "blocked" : watchBlockers.length ? "watch" : "stable",
    lecReady: productionBlockers.length === 0,
    jarvisReady: productionBlockers.length === 0,
    missingInputs: customDeltaTruth.openQuestions,
    rationale: ["Legacy contract still tracks blockers, but not yet the explicit v3 handoff split."],
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white/95 p-4 shadow-[0_28px_80px_rgba(15,23,42,0.06)]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
            {foundryContractV3 ? "Assembly contract v3" : "Assembly contract v2.5"}
          </div>
          <h3 className="mt-2 text-lg font-semibold text-slate-950">Foundry freezes the assembly truth before Jarvis execution</h3>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Archetype, domain kit, security pack, QA pack, custom delta, production blockers, and runtime constraints stay explicit so Jarvis assembles instead of redesigning.
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-right">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Contract posture</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{contractPosture.replace(/-/g, " ")}</div>
          <div className="text-xs text-slate-600">
            {assemblyPlan.assemblyOrder.length} steps | {productionBlockers.length} production blockers
          </div>
        </div>
      </div>

      {foundryContractV3 ? (
        <div className="mt-4 grid gap-3 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Completeness</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${postureTone(foundryContractV3.specCompleteness.status)}`}>
                {foundryContractV3.specCompleteness.status}
              </span>
              <span className="text-sm font-semibold text-slate-950">{foundryContractV3.specCompleteness.score}/100</span>
            </div>
            <div className="mt-3 text-xs text-slate-600">
              {foundryContractV3.specCompleteness.sections.length} sections reviewed
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">External ingestion</div>
            <div className="mt-2 flex items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${postureTone(foundryContractV3.externalIngestion.status)}`}>
                {foundryContractV3.externalIngestion.status}
              </span>
              <span className="text-sm font-semibold text-slate-950">{foundryContractV3.externalIngestion.score}/100</span>
            </div>
            <div className="mt-3 text-xs text-slate-600">{foundryContractV3.externalIngestion.laneTitle}</div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Handoff stability</div>
            <div className="mt-2">
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${postureTone(handoffStability.status)}`}>
                {handoffStability.status}
              </span>
            </div>
            <div className="mt-3 text-xs text-slate-600">
              LEC {handoffStability.lecReady ? "ready" : "blocked"} | Jarvis {handoffStability.jarvisReady ? "ready" : "blocked"}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Runtime constraints</div>
            <div className="mt-2 text-sm font-semibold text-slate-950">{runtimeConstraints.length}</div>
            <div className="mt-3 text-xs text-slate-600">Deployment, auth, observability, compliance, and env truth are locked explicitly.</div>
          </div>
        </div>
      ) : null}

      <div className="mt-4 grid gap-3 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(140deg,rgba(15,23,42,0.02),rgba(59,130,246,0.08))] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Archetype selection</div>
          <div className="mt-2 text-base font-semibold text-slate-950">{foundryContract.archetypeSelection.title}</div>
          <div className="mt-1 text-xs text-slate-600">
            {foundryContract.archetypeSelection.layoutFamily} | {foundryContract.archetypeSelection.workflowFamily}
          </div>
          <p className="mt-3 text-sm text-slate-700">{foundryContract.archetypeSelection.summary}</p>
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white/80 p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Experience thesis</div>
            <div className="mt-2 text-sm font-semibold text-slate-950">{foundryContract.archetypeSelection.experienceLabel}</div>
            <p className="mt-2 text-xs leading-5 text-slate-600">{foundryContract.archetypeSelection.heroNarrative}</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(140deg,rgba(16,185,129,0.08),rgba(15,23,42,0.02))] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Domain kit</div>
          <div className="mt-2 text-base font-semibold text-slate-950">{foundryContract.domainKitSelection.title}</div>
          <div className="mt-1 text-xs text-slate-600">
            {foundryContract.domainKitSelection.domainFamily} | {foundryContract.domainKitSelection.operatingModel}
          </div>
          <p className="mt-3 text-sm text-slate-700">{foundryContract.domainKitSelection.summary}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {foundryContract.domainKitSelection.selectedSliceIds.map((sliceId) => (
              <span key={sliceId} className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] text-emerald-700">
                {sliceId}
              </span>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(140deg,rgba(249,115,22,0.08),rgba(15,23,42,0.02))] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Security pack</div>
          <div className="mt-2 text-base font-semibold text-slate-950">{foundryContract.securityPackSelection.title}</div>
          <div className="mt-1 text-xs text-slate-600">
            {foundryContract.securityPackSelection.controlIds.length} controls | {foundryContract.securityPackSelection.approvalIds.length} approvals
          </div>
          <div className="mt-3 grid gap-2 text-xs text-slate-600">
            <div>RBAC template: {foundryContract.securityPackSelection.rbacTemplateId}</div>
            <div>Tenant isolation: {foundryContract.securityPackSelection.tenantIsolationMode}</div>
            <div>Secret posture: {foundryContract.securityPackSelection.secretHandlingMode}</div>
            <div>SSO posture: {foundryContract.securityPackSelection.ssoProfile}</div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-[linear-gradient(140deg,rgba(99,102,241,0.08),rgba(15,23,42,0.02))] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">QA pack</div>
          <div className="mt-2 text-base font-semibold text-slate-950">{foundryContract.qaPackSelection.title}</div>
          <div className="mt-1 text-xs text-slate-600">
            {foundryContract.qaPackSelection.primaryFlowIds.length} primary flows | {foundryContract.qaPackSelection.seededDataNeeds.length} seed needs
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {foundryContract.qaPackSelection.primaryFlowIds.map((flowId) => (
              <span key={flowId} className="rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] text-indigo-700">
                {flowId}
              </span>
            ))}
          </div>
          <div className="mt-3 text-xs text-slate-600">
            Evidence: {foundryContract.qaPackSelection.evidenceArtifacts.join(", ")}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-[1fr,1fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Custom delta</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700">
              {customDeltaTruth.load}
            </span>
            <span className="text-sm font-semibold text-slate-950">{customDeltaTruth.summary}</span>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">UI requirements</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {customDeltaTruth.customUiRequirements.map((item) => (
                  <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Integrations</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {customDeltaTruth.customIntegrations.map((item) => (
                  <span key={item} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Open questions</div>
            <div className="mt-2 space-y-2">
              {customDeltaTruth.openQuestions.length ? (
                customDeltaTruth.openQuestions.map((item) => (
                  <div key={item} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {item}
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  No open custom-delta question remains.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Runtime constraints</div>
          <div className="mt-3 space-y-3">
            {runtimeConstraints.length ? (
              runtimeConstraints.map((constraint) => (
                <div key={constraint.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="text-sm font-semibold text-slate-950">{constraint.title}</div>
                  <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{constraint.value}</div>
                  <div className="mt-2 text-xs text-slate-600">{constraint.reason}</div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                Runtime constraints are not split explicitly on this draft yet.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 lg:grid-cols-[1.05fr_0.95fr]">
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Production blockers</div>
            <div className="text-xs text-slate-600">{productionBlockers.length} blocking</div>
          </div>
          <div className="mt-3 space-y-3">
            {productionBlockers.length ? (
              productionBlockers.map((blocker) => (
                <div key={blocker.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${blockerTone(blocker.severity)}`}>
                      {blocker.severity}
                    </span>
                    <span className="text-xs uppercase tracking-wide text-slate-500">{blocker.source}</span>
                  </div>
                  <div className="mt-2 text-sm font-semibold text-slate-900">{blocker.title}</div>
                  <div className="mt-1 text-xs text-slate-600">{blocker.resolution}</div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-700">
                No blocking production blocker remains in the contract.
              </div>
            )}
            {watchBlockers.length ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Watch list</div>
                <div className="mt-2 space-y-2">
                  {watchBlockers.map((blocker) => (
                    <div key={blocker.id} className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      {blocker.title}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Assembly order</div>
            <div className="text-xs text-slate-600">{assemblyPlan.assemblyOrder.length} ordered steps</div>
          </div>
          <div className="mt-3 space-y-3">
            {assemblyPlan.assemblyOrder.map((step) => (
              <div key={step.id} className="rounded-2xl border border-slate-200 bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                    {step.kind}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-slate-500">{step.source}</span>
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">{step.title}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {step.scopeIds.map((scopeId) => (
                    <span key={scopeId} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-700">
                      {scopeId}
                    </span>
                  ))}
                </div>
                <div className="mt-2 text-xs text-slate-600">{step.acceptanceCriteria.join(" | ")}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
